/**
 * snabborder.js — Jolat Snabborder (Agent E). Vanilla ES2019, defer.
 *
 * Två lägen i samma <jolat-snabborder>-element:
 *   Läge A "Sök & lägg till": autocompleterad sökrad (predictive suggest med
 *     variants.sku-fält) → rader i tabell med variantväljare, antal, à-pris.
 *   Läge B "Klistra in lista": textarea → parser med synonymgrupper → matchning
 *     via /search/suggest.json + variant-scoring → granskningssteg per rad →
 *     "Lägg allt i varukorgen".
 *
 * Parser, synonymgrupper och variant-scoring är PORTADE från gamla temats
 * assets/jolat-snabborder.js (beprövad logik) men skriven ren mot nya Jolat-API:t.
 *
 * Summa + vikt + fraktestimat visas innan add (Jolat.money / Jolat.shippingEstimate).
 * Senaste klistrade lista sparas i localStorage 'jolat_snabborder_draft'.
 *
 * Beroenden: window.Jolat (Agent G: money/displayOre/cart.add/shippingEstimate/openCart).
 * Fungerar även utan inloggning (B2C-snabbköp).
 */
(function () {
  'use strict';

  var SEARCH_LIMIT = 3;
  var MATCH_DELAY_MS = 80;          // throttle mot rate-limit
  var SUGGEST_DEBOUNCE_MS = 200;
  var DRAFT_KEY = 'jolat_snabborder_draft';

  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  /* ====================================================================== */
  /* Synonymgrupper (svenska + engelska, med pluralformer) — PORT.          */
  /* Index 0–12 = förpackning/form/styck, 13+ = färg.                       */
  /* ====================================================================== */
  var SYNONYM_GROUPS = [
    // 0  bulk: kartong/låda/box/case
    ['krt', 'kart', 'kartong', 'kartonger', 'kartongen', 'kartongerna',
     'lada', 'låda', 'lador', 'lådor', 'lådan', 'lådorna',
     'box', 'boxes', 'carton', 'cartons', 'case', 'cases'],
    // 1  single: st/styck/pcs
    ['st', 'styck', 'stycken', 'stk', 'stck', 'stykke',
     'enstaka', 'single', 'singel', 'pcs', 'pc', 'piece', 'pieces'],
    // 2  form: patron
    ['patron', 'patroner', 'patronen', 'patronerna', 'cartridge', 'cartridges'],
    // 3  form: påse
    ['pase', 'påse', 'pasar', 'påsar', 'påsen', 'påsarna', 'pouch', 'pouches', 'bag', 'bags'],
    // 4  form: korv
    ['korv', 'korvar', 'korven', 'korvarna', 'sausage', 'foilpack', 'foilbag'],
    // 5  form: paket/förpackning
    ['pkt', 'paket', 'paketet', 'paketen', 'paketer',
     'förpackning', 'förpackningar', 'forpackning', 'forpackningar',
     'package', 'packages', 'pack', 'packs'],
    // 6  form: hink
    ['hink', 'hinkar', 'hinken', 'hinkarna', 'bucket', 'buckets', 'pail', 'pails'],
    // 7  form: tub
    ['tub', 'tuber', 'tuben', 'tuberna', 'tube', 'tubes'],
    // 8  form: rulle
    ['rulle', 'rullar', 'rullen', 'rullarna', 'roll', 'rolls'],
    // 9  form: flaska
    ['flaska', 'flaskor', 'flaskan', 'flaskorna', 'bottle', 'bottles'],
    // 10 form: burk
    ['burk', 'burkar', 'burken', 'burkarna', 'jar', 'jars', 'can', 'cans', 'tin', 'tins'],
    // 11 form: säck
    ['säck', 'sack', 'säckar', 'sackar', 'säcken', 'säckarna', 'sacks'],
    // 12 bulk: pall
    ['pall', 'pallar', 'pallen', 'pallarna', 'pallet', 'pallets'],

    // Färger — gråtoner
    ['grå', 'gra', 'grey', 'gray', 'greys', 'grays'],
    ['mörkgrå', 'morkgra', 'mork grå', 'mörk grå', 'darkgrey', 'darkgray', 'dark grey', 'dark gray'],
    ['ljusgrå', 'ljusgra', 'ljus grå', 'ljus gra', 'lightgrey', 'lightgray', 'light grey', 'light gray'],
    ['silver', 'silvergrå', 'silvergray', 'silvergrey'],
    ['betonggrå', 'betong', 'cement', 'cementgrå', 'concrete', 'concretegrey', 'concretegray'],
    ['antracit', 'antracitgrå', 'anthracite'],
    // Färger — bas
    ['vit', 'vita', 'vitt', 'white', 'whites'],
    ['svart', 'svarta', 'black', 'blacks'],
    ['brun', 'bruna', 'brown', 'browns'],
    ['mörkbrun', 'morkbrun', 'darkbrown'],
    ['beige', 'sandbeige', 'sand', 'sandgrå'],
    ['transparent', 'klar', 'klart', 'clear', 'opal', 'genomskinlig', 'genomskinligt'],
    // Färger — kulörer
    ['röd', 'rod', 'röda', 'red', 'reds', 'tegelröd'],
    ['blå', 'bla', 'blue', 'blues'],
    ['mörkblå', 'morkbla', 'mörk blå', 'darkblue', 'dark blue'],
    ['ljusblå', 'ljusbla', 'ljus blå', 'lightblue', 'light blue'],
    ['grön', 'gron', 'gröna', 'green', 'greens'],
    ['gul', 'gula', 'yellow', 'yellows'],
    // Färger — träslag
    ['ek', 'oak'],
    ['björk', 'birch', 'bjork'],
    ['furu', 'fur', 'pine'],
    ['mahogny', 'mahogany'],
    ['valnöt', 'valnot', 'walnut']
  ];

  function _groupType(gi) { return gi <= 12 ? 'pack' : 'color'; }
  function _packKind(gi) {
    if (gi === 0 || gi === 12) return 'bulk';
    if (gi === 1) return 'single';
    return 'form';
  }

  var _WORD_CHARS = 'a-zA-Z0-9åäöÅÄÖéüÉÜ';
  function _escapeRegex(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  function _hasHintWord(blob, hint) {
    if (!hint) return false;
    var re = new RegExp('(?:^|[^' + _WORD_CHARS + '])' + _escapeRegex(hint) + '(?:[^' + _WORD_CHARS + ']|$)', 'i');
    return re.test(blob);
  }
  function _hasHintLoose(blob, hint) {
    if (_hasHintWord(blob, hint)) return true;
    if (!/\d/.test(hint)) return false;
    var squashedBlob = blob.replace(/\s+/g, '');
    var squashedHint = hint.replace(/\s+/g, '');
    if (squashedHint.length < 3) return false;
    var re = new RegExp('(?:^|[^0-9])' + _escapeRegex(squashedHint), 'i');
    return re.test(squashedBlob);
  }

  var SYNONYM_LOOKUP = (function () {
    var lookup = {};
    SYNONYM_GROUPS.forEach(function (group, gi) {
      var type = _groupType(gi);
      var kind = type === 'pack' ? _packKind(gi) : 'color';
      var syns = group.map(function (t) { return t.toLowerCase(); });
      group.forEach(function (term) {
        lookup[term.toLowerCase()] = { type: type, kind: kind, synonyms: syns };
      });
    });
    return lookup;
  })();

  function isVariantKeyword(token) { return !!SYNONYM_LOOKUP[token.toLowerCase()]; }
  function expandSynonyms(token) {
    var entry = SYNONYM_LOOKUP[token.toLowerCase()];
    return entry ? entry.synonyms : [token.toLowerCase()];
  }
  function tokenType(token) {
    var entry = SYNONYM_LOOKUP[token.toLowerCase()];
    return entry ? entry.type : 'other';
  }
  function variantIsBulk(blobStr) {
    return /(kartong|krt|kart|l[åa]d|box|carton|case|pall)/i.test(blobStr);
  }
  function tokenizeHints(text) {
    return text.toLowerCase().replace(/[()]/g, ' ').split(/[\s,;]+/).filter(Boolean);
  }

  /* ====================================================================== */
  /* Förpacknings-ordlistor — PORT (domänkunskap: hur Jolats kunder skriver) */
  /*  BULK (kartong): kartong/krt/kart/låda/box/pall/paket/pack OCH abbr "st".*/
  /*  SINGLE (lös vara): hela ordet stycke/stycken/styck (+ pcs/single/...).  */
  /*  FORM (fysisk lös enhet → sätter single OCH text-matchar formen):        */
  /*       patron/påse/korv/hink/tub/rulle/flaska/burk/säck.                   */
  /*  OBS: "st" → kartong men "stycken" → lös vara. Medvetet, inte bugg.      */
  /* ====================================================================== */
  function _wordSet(arr) { var o = {}; arr.forEach(function (w) { o[w] = true; }); return o; }
  var BULK_WORDS = _wordSet([
    'kartong', 'kartonger', 'kartongen', 'kartongerna', 'krt', 'kart',
    'lada', 'låda', 'lador', 'lådor', 'lådan', 'lådorna',
    'box', 'boxes', 'carton', 'cartons', 'case', 'cases',
    'pall', 'pallar', 'pallen', 'pallarna', 'pallet', 'pallets',
    'pkt', 'paket', 'paketet', 'paketen', 'paketer',
    'förpackning', 'förpackningar', 'forpackning', 'forpackningar', 'package', 'packages', 'pack', 'packs',
    'st'
  ]);
  var SINGLE_WORDS = _wordSet([
    'stycke', 'stycken', 'styck', 'stk', 'stck', 'stykke', 'enstaka', 'single', 'singel', 'pcs', 'pc', 'piece', 'pieces'
  ]);
  var FORM_WORDS = _wordSet([
    'patron', 'patroner', 'patronen', 'patronerna', 'cartridge', 'cartridges',
    'påse', 'pase', 'påsar', 'pasar', 'påsen', 'påsarna', 'pouch', 'pouches', 'bag', 'bags',
    'korv', 'korvar', 'korven', 'korvarna', 'sausage', 'foilpack', 'foilbag',
    'hink', 'hinkar', 'hinken', 'hinkarna', 'bucket', 'buckets', 'pail', 'pails',
    'tub', 'tuber', 'tuben', 'tuberna', 'tube', 'tubes',
    'rulle', 'rullar', 'rullen', 'rullarna', 'roll', 'rolls',
    'flaska', 'flaskor', 'flaskan', 'flaskorna', 'bottle', 'bottles',
    'burk', 'burkar', 'burken', 'burkarna', 'jar', 'jars', 'can', 'cans', 'tin', 'tins',
    'säck', 'sack', 'säckar', 'sackar', 'säcken', 'säckarna', 'sacks'
  ]);

  /* "20-pack"/"20pack" är en kartong-beteckning, INTE antal 20 → ersätt med
     'kartong' så siffran inte tolkas som kvantitet och ordet ger bulk-avsikt.
     Endast bindestreck/hopskrivet normaliseras; "12 pack" (med blanksteg)
     lämnas så att 12 förblir kvantitet. PORT. */
  function _normalizePackPhrases(s) {
    return s.replace(/\b\d+\s*-\s*pack\w*/gi, 'kartong').replace(/\b\d+pack\w*/gi, 'kartong');
  }

  /* ====================================================================== */
  /* Parser — PORT. Greedy: sista talet på raden = antal.                   */
  /* ====================================================================== */
  function parseInput(text) {
    var lines = text.split(/\r?\n/);
    var rows = [];
    var QTY_RE = /^(.+)[\s,;]+(\d+)(?:[\s,;]+(.+))?$/;
    for (var i = 0; i < lines.length; i++) {
      var rawLine = lines[i];
      var line = rawLine.trim();
      if (!line) continue;
      var article, qty, hintTokens = [];

      if (rawLine.indexOf('\t') !== -1) {
        // Excel-inklistring: Artikelnummer | Antal | Storlek | Färg | Förpackning
        var cols = rawLine.split('\t').map(function (c) { return c.trim(); });
        article = cols[0];
        qty = parseInt(cols[1], 10);
        if (isNaN(qty)) qty = 1;
        hintTokens = tokenizeHints(_normalizePackPhrases(cols.slice(2).join(' ')));
      } else {
        // Normalisera EN gång: "20-pack"/"20pack" → "kartong". Använd den
        // normaliserade strängen både för QTY_RE OCH för else-grenens artikel,
        // så bare pack-fraser ("SP519 20-pack") bevarar kartong-avsikten
        // (annars går bulk-signalen förlorad i variant-scoringen). raw nedan
        // förblir oförändrad line så kunden ser sin egen text.
        var norm = _normalizePackPhrases(line);
        var m = norm.match(QTY_RE);
        if (m) {
          article = m[1].trim();
          qty = parseInt(m[2], 10);
          var rest = (m[3] || '').trim();
          if (rest) hintTokens = tokenizeHints(rest);
        } else {
          article = norm;
          qty = 1;
        }
      }
      if (!article) continue;
      if (qty < 1) qty = 1;

      // Plocka av variant-nyckelord på slutet av artikeln ("SP519 grå" → grå)
      var tokens = article.split(/\s+/);
      while (tokens.length > 1 && isVariantKeyword(tokens[tokens.length - 1])) {
        hintTokens.unshift(tokens.pop().toLowerCase());
      }
      article = tokens.join(' ');

      rows.push({
        idx: rows.length, raw: line, article: article, qty: qty,
        hintTokens: hintTokens, status: 'pending', match: null,
        alternatives: [], missingHints: [], variantOutOfStock: false, removed: false
      });
    }
    return rows;
  }

  /* ====================================================================== */
  /* Variant-scoring — PORT. Färg via coverage, förpackning via avsikt.     */
  /* ====================================================================== */
  function variantBlob(v) {
    return [v.title, v.option1, v.option2, v.option3, v.name].filter(Boolean).join(' ').toLowerCase();
  }
  function optionValues(v) {
    if (v.options && v.options.length) return v.options;
    return (v.title || '').split('/').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function pickVariant(variants, hintTokens) {
    if (!variants || variants.length === 0) return null;
    if (variants.length === 1) return variants[0];

    if (!hintTokens || hintTokens.length === 0) {
      return variants.find(function (v) { return !variantIsBulk(variantBlob(v)) && v.available; })
        || variants.find(function (v) { return v.available; })
        || variants.find(function (v) { return !variantIsBulk(variantBlob(v)); })
        || variants[0];
    }

    // Förpackningsavsikt via ordlistorna (domänkunskap): "st" → bulk/kartong,
    // "stycken" → lös vara. FORM-ord (påse/patron/korv...) sätter single OCH
    // text-matchas mot varianttiteln. PORT.
    var packIntent = 'none';
    var colorWordSet = {};
    var formTokens = [];
    hintTokens.forEach(function (token) {
      var low = token.toLowerCase();
      if (BULK_WORDS[low]) {
        packIntent = 'bulk';
      } else if (SINGLE_WORDS[low]) {
        if (packIntent !== 'bulk') packIntent = 'single';
      } else if (FORM_WORDS[low]) {
        if (packIntent !== 'bulk') packIntent = 'single';
        formTokens.push(low);
      } else {
        expandSynonyms(token).forEach(function (s) { colorWordSet[s] = true; });
        colorWordSet[low] = true;
      }
    });

    // Viktnivåer (medvetet åtskilda så prioriteringen är entydig) — PORT:
    //   FÄRG (100-skala) ≫ FORM (30) > LAGER (+10) > förpacknings-avsikt (±4).
    // Konsekvens: rätt FÄRG vinner alltid; inom samma färg vinner den som
    // finns I LAGER (så lös-vara-term landar på köpbar kartong när styck-
    // varianten är slut). Är HELA färgen slut väljs ändå rätt färg och flaggas.
    function colorScore(v) {
      var best = 0;
      optionValues(v).forEach(function (val) {
        var words = String(val).toLowerCase().split(/\s+/).filter(Boolean);
        if (!words.length) return;
        var covered = 0;
        words.forEach(function (w) { if (colorWordSet[w]) covered += 1; });
        if (covered === 0) return;
        var sc = (covered === words.length) ? (100 + (words.length - 1) * 40) : (covered * 20);
        if (sc > best) best = sc;
      });
      return best;
    }
    function formScore(v) {
      if (!formTokens.length) return 0;
      var b = variantBlob(v);
      var s = 0;
      formTokens.forEach(function (token) {
        if (expandSynonyms(token).some(function (syn) { return _hasHintLoose(b, syn); })) s += 30;
      });
      return s;
    }
    function packScore(isBulk) {
      if (packIntent === 'single') return isBulk ? -4 : 4;
      if (packIntent === 'bulk') return isBulk ? 4 : -4;
      return 0;
    }

    var scored = variants.map(function (v) {
      var isBulk = variantIsBulk(variantBlob(v));
      var aScore = v.available ? 10 : 0;   // i-lager-varianter prioriteras — PORT
      return {
        variant: v,
        score: colorScore(v) + formScore(v) + aScore + packScore(isBulk),
        isBulk: isBulk,
        available: !!v.available
      };
    });
    scored.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      if (a.available !== b.available) return a.available ? -1 : 1;
      if (a.isBulk !== b.isBulk) return a.isBulk ? 1 : -1;
      return 0;
    });
    return scored[0] ? scored[0].variant : variants[0];
  }

  /* ====================================================================== */
  /* Pris/vikt-hjälpare via Jolat                                           */
  /* ====================================================================== */
  function money(ore) {
    if (window.Jolat && window.Jolat.money) return window.Jolat.money(window.Jolat.displayOre(ore));
    return (Math.round(ore || 0) / 100).toLocaleString('sv-SE') + ' kr';
  }
  function weightText(grams) {
    if (window.Jolat && window.Jolat.formatWeight) return window.Jolat.formatWeight(grams);
    return (Math.round((grams || 0) / 100) / 10).toLocaleString('sv-SE') + ' kg';
  }
  function shippingText(grams) {
    if (window.JolatShipping && window.JolatShipping.text) return window.JolatShipping.text(grams);
    if (window.Jolat && window.Jolat.shippingEstimate) {
      var est = window.Jolat.shippingEstimate(grams);
      if (est) return 'Frakt från ' + window.Jolat.money(est.priceOre) + ' (' + est.method + ')';
    }
    return '';
  }

  /* ====================================================================== */
  /* Sök-API                                                                */
  /* ====================================================================== */
  function suggestUrl(q, limit) {
    return '/search/suggest.json?q=' + encodeURIComponent(q)
      + '&resources[type]=product'
      + '&resources[limit]=' + (limit || SEARCH_LIMIT)
      + '&resources[options][unavailable_products]=last'
      + '&resources[options][fields]=' + encodeURIComponent('title,product_type,variants.title,variants.sku,vendor,tag');
  }

  function fetchProducts(q, limit) {
    return fetch(suggestUrl(q, limit), { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('search'); return r.json(); })
      .then(function (data) {
        return (data && data.resources && data.resources.results && data.resources.results.products) || [];
      });
  }

  function fetchVariants(match) {
    var jsUrl = null;
    if (match.url) jsUrl = match.url.split('?')[0] + '.js';
    else if (match.handle) jsUrl = '/products/' + match.handle + '.js';
    if (!jsUrl) return Promise.resolve(null);
    return fetch(jsUrl, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /* ====================================================================== */
  /* Custom element                                                         */
  /* ====================================================================== */
  function svgRemove() {
    return '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12"/><path d="M18 6 6 18"/></svg>';
  }

  class JolatSnabborder extends HTMLElement {
    constructor() {
      super();
      this.rows = [];
      this._matching = false;
      this._searchRows = [];   // Läge A
      this._suggestTimer = null;
      this._suggestAbort = null;
    }

    connectedCallback() {
      // Tabbar
      this.tabBtns = Array.prototype.slice.call(this.querySelectorAll('[data-mode-tab]'));
      this.panels = {
        search: this.querySelector('[data-mode-panel="search"]'),
        paste: this.querySelector('[data-mode-panel="paste"]')
      };
      this.tabBtns.forEach(function (btn) {
        btn.addEventListener('click', this._onModeTab.bind(this));
      }.bind(this));

      // Läge B – paste
      this.textarea = this.querySelector('[data-sb-input]');
      this.matchBtn = this.querySelector('[data-sb-match]');
      this.reviewWrap = this.querySelector('[data-sb-review]');
      this.tbody = this.querySelector('[data-sb-tbody]');
      this.errorEl = this.querySelector('[data-sb-error]');
      this.summaryEl = this.querySelector('[data-sb-summary]');
      this.totalsEl = this.querySelector('[data-sb-totals]');
      this.addBtn = this.querySelector('[data-sb-add]');
      this.resetBtn = this.querySelector('[data-sb-reset]');
      this.progressEl = this.querySelector('[data-sb-progress]');

      if (this.matchBtn) this.matchBtn.addEventListener('click', this._onMatch.bind(this));
      if (this.addBtn) this.addBtn.addEventListener('click', this._onAddPaste.bind(this));
      if (this.resetBtn) this.resetBtn.addEventListener('click', this._onReset.bind(this));

      // Läge A – sök
      this.searchInput = this.querySelector('[data-sb-search]');
      this.suggestList = this.querySelector('[data-sb-suggest]');
      this.searchTbody = this.querySelector('[data-sb-search-tbody]');
      this.searchTotals = this.querySelector('[data-sb-search-totals]');
      this.searchAddBtn = this.querySelector('[data-sb-search-add]');
      this.searchEmpty = this.querySelector('[data-sb-search-empty]');
      if (this.searchInput) {
        this.searchInput.addEventListener('input', this._onSuggestInput.bind(this));
        this.searchInput.addEventListener('keydown', this._onSuggestKeydown.bind(this));
        this.searchInput.addEventListener('blur', function () {
          setTimeout(function () { this._closeSuggest(); }.bind(this), 150);
        }.bind(this));
      }
      if (this.searchAddBtn) this.searchAddBtn.addEventListener('click', this._onAddSearch.bind(this));

      // Återställ utkast
      if (this.textarea) {
        try {
          var draft = window.localStorage.getItem(DRAFT_KEY);
          if (draft) this.textarea.value = draft;
        } catch (e) { /* privat läge */ }
        this.textarea.addEventListener('input', function () {
          try { window.localStorage.setItem(DRAFT_KEY, this.textarea.value); } catch (e) {}
        }.bind(this));
      }

      this._renderSearchTable();
    }

    /* ---- Lägesväxling -------------------------------------------------- */
    _onModeTab(e) {
      var mode = e.currentTarget.getAttribute('data-mode-tab');
      this.tabBtns.forEach(function (btn) {
        var active = btn.getAttribute('data-mode-tab') === mode;
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
        btn.tabIndex = active ? 0 : -1;
      });
      Object.keys(this.panels).forEach(function (key) {
        if (this.panels[key]) this.panels[key].hidden = key !== mode;
      }.bind(this));
    }

    /* ===================================================================== */
    /* LÄGE A — Sök & lägg till                                             */
    /* ===================================================================== */
    _onSuggestInput() {
      var q = this.searchInput.value.trim();
      window.clearTimeout(this._suggestTimer);
      if (q.length < 2) { this._closeSuggest(); return; }
      this._suggestTimer = window.setTimeout(function () {
        if (this._suggestAbort) this._suggestAbort.abort();
        this._suggestAbort = new AbortController();
        fetch(suggestUrl(q, 6), { headers: { Accept: 'application/json' }, signal: this._suggestAbort.signal })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
            this._renderSuggest(products);
          }.bind(this))
          .catch(function () {});
      }.bind(this), SUGGEST_DEBOUNCE_MS);
    }

    _renderSuggest(products) {
      if (!this.suggestList) return;
      this.suggestList.replaceChildren();
      if (!products.length) { this._closeSuggest(); return; }
      var self = this;
      products.slice(0, 6).forEach(function (p, i) {
        var li = document.createElement('li');
        li.className = 'jl-sb__suggest-item';
        li.setAttribute('role', 'option');
        li.id = 'sb-sugg-' + i;
        li.tabIndex = -1;

        var img = document.createElement('span');
        img.className = 'jl-sb__suggest-img';
        var src = p.image || (p.featured_image && p.featured_image.url) || '';
        if (src) {
          var im = document.createElement('img');
          im.src = src; im.alt = ''; im.loading = 'lazy';
          img.appendChild(im);
        }
        li.appendChild(img);

        var body = document.createElement('span');
        body.className = 'jl-sb__suggest-body';
        var t = document.createElement('span');
        t.className = 'jl-sb__suggest-title';
        t.textContent = p.title || '';
        body.appendChild(t);
        if (p.vendor) {
          var v = document.createElement('span');
          v.className = 'jl-sb__suggest-vendor';
          v.textContent = p.vendor;
          body.appendChild(v);
        }
        li.appendChild(body);

        li.addEventListener('mousedown', function (e) {
          e.preventDefault();
          self._addSearchProduct(p);
        });
        self.suggestList.appendChild(li);
      });
      this.suggestList.hidden = false;
      this.searchInput.setAttribute('aria-expanded', 'true');
    }

    _closeSuggest() {
      if (this.suggestList) { this.suggestList.hidden = true; this.suggestList.replaceChildren(); }
      if (this.searchInput) this.searchInput.setAttribute('aria-expanded', 'false');
      this._suggestActive = -1;
    }

    _onSuggestKeydown(e) {
      if (!this.suggestList || this.suggestList.hidden) return;
      var items = Array.prototype.slice.call(this.suggestList.children);
      if (!items.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        var cur = this._suggestActive == null ? -1 : this._suggestActive;
        cur += e.key === 'ArrowDown' ? 1 : -1;
        if (cur < 0) cur = items.length - 1;
        if (cur >= items.length) cur = 0;
        this._suggestActive = cur;
        items.forEach(function (it, i) { it.classList.toggle('is-active', i === cur); });
        this.searchInput.setAttribute('aria-activedescendant', items[cur].id);
      } else if (e.key === 'Enter' && this._suggestActive != null && this._suggestActive >= 0) {
        e.preventDefault();
        items[this._suggestActive].dispatchEvent(new MouseEvent('mousedown'));
      } else if (e.key === 'Escape') {
        this._closeSuggest();
      }
    }

    _addSearchProduct(product) {
      this._closeSuggest();
      this.searchInput.value = '';
      var row = {
        product: product, variants: null, selectedId: null, qty: 1, loading: true
      };
      this._searchRows.push(row);
      this._renderSearchTable();
      fetchVariants(product).then(function (detail) {
        row.loading = false;
        if (detail && detail.variants) {
          row.variants = detail.variants;
          var preferred = pickVariant(detail.variants, []);
          row.selectedId = preferred ? preferred.id : (detail.variants[0] && detail.variants[0].id);
          product.handle = detail.handle || product.handle;
        }
        this._renderSearchTable();
      }.bind(this));
    }

    _searchVariant(row) {
      if (!row.variants) return null;
      for (var i = 0; i < row.variants.length; i++) {
        if (row.variants[i].id === row.selectedId) return row.variants[i];
      }
      return row.variants[0] || null;
    }

    _renderSearchTable() {
      if (!this.searchTbody) return;
      var self = this;
      var frag = document.createDocumentFragment();

      this._searchRows.forEach(function (row, idx) {
        var tr = document.createElement('tr');
        var v = self._searchVariant(row);

        // Bild + titel
        var c1 = document.createElement('td');
        c1.className = 'jl-sb__cell-product';
        var src = row.product.image || (row.product.featured_image && row.product.featured_image.url) || '';
        if (src) {
          var im = document.createElement('img');
          im.className = 'jl-sb__thumb'; im.src = src; im.alt = ''; im.loading = 'lazy';
          c1.appendChild(im);
        } else {
          var ph = document.createElement('span');
          ph.className = 'jl-sb__thumb jl-sb__thumb--empty';
          c1.appendChild(ph);
        }
        var ti = document.createElement('span');
        ti.className = 'jl-sb__cell-title';
        ti.textContent = row.product.title || '';
        c1.appendChild(ti);
        tr.appendChild(c1);

        // Variantväljare
        var c2 = document.createElement('td');
        if (row.loading) {
          c2.textContent = '…';
        } else if (row.variants && row.variants.length > 1) {
          var sel = document.createElement('select');
          sel.className = 'jl-select jl-sb__variant-select';
          sel.setAttribute('aria-label', 'Variant');
          row.variants.forEach(function (vv) {
            var opt = document.createElement('option');
            opt.value = String(vv.id);
            opt.textContent = (vv.title && vv.title !== 'Default Title' ? vv.title : 'Standard')
              + (vv.available ? '' : ' (slut)');
            if (vv.id === row.selectedId) opt.selected = true;
            sel.appendChild(opt);
          });
          sel.addEventListener('change', function (e) {
            row.selectedId = parseInt(e.target.value, 10);
            self._renderSearchTable();
          });
          c2.appendChild(sel);
        } else if (v && v.title && v.title !== 'Default Title') {
          c2.textContent = v.title;
        } else {
          c2.textContent = '—';
        }
        tr.appendChild(c2);

        // Antal
        var c3 = document.createElement('td');
        var qty = document.createElement('input');
        qty.type = 'number'; qty.min = '1'; qty.value = String(row.qty);
        qty.className = 'jl-input jl-sb__qty'; qty.setAttribute('aria-label', 'Antal');
        qty.addEventListener('input', function (e) {
          var n = parseInt(e.target.value, 10);
          row.qty = isNaN(n) || n < 1 ? 1 : n;
          self._renderSearchTotals();
        });
        c3.appendChild(qty);
        tr.appendChild(c3);

        // à-pris
        var c4 = document.createElement('td');
        c4.className = 'jl-sb__cell-price';
        if (v && typeof v.price === 'number') c4.textContent = money(v.price);
        else c4.textContent = '—';
        tr.appendChild(c4);

        // Radsumma
        var c5 = document.createElement('td');
        c5.className = 'jl-sb__cell-price';
        if (v && typeof v.price === 'number') c5.textContent = money(v.price * row.qty);
        else c5.textContent = '—';
        tr.appendChild(c5);

        // Ta bort
        var c6 = document.createElement('td');
        c6.className = 'jl-sb__cell-action';
        var rm = document.createElement('button');
        rm.type = 'button'; rm.className = 'jl-sb__remove';
        rm.setAttribute('aria-label', 'Ta bort rad'); rm.innerHTML = svgRemove();
        rm.addEventListener('click', function () {
          self._searchRows.splice(idx, 1);
          self._renderSearchTable();
        });
        c6.appendChild(rm);
        tr.appendChild(c6);

        frag.appendChild(tr);
      });

      this.searchTbody.replaceChildren(frag);
      if (this.searchEmpty) this.searchEmpty.hidden = this._searchRows.length > 0;
      this._renderSearchTotals();
    }

    _renderSearchTotals() {
      var totalOre = 0, totalGrams = 0, count = 0;
      this._searchRows.forEach(function (row) {
        var v = this._searchVariant(row);
        if (v && typeof v.price === 'number') {
          totalOre += v.price * row.qty;
          totalGrams += (v.grams || 0) * row.qty;
          count += row.qty;
        }
      }.bind(this));
      if (this.searchTotals) this._fillTotals(this.searchTotals, totalOre, totalGrams, count);
      if (this.searchAddBtn) {
        this.searchAddBtn.disabled = count === 0;
        var lbl = this.searchAddBtn.querySelector('[data-sb-add-label]') || this.searchAddBtn;
        lbl.textContent = count > 0 ? ('Lägg ' + count + ' i varukorgen') : 'Lägg i varukorgen';
      }
    }

    _onAddSearch() {
      var items = [];
      this._searchRows.forEach(function (row) {
        var v = this._searchVariant(row);
        if (v && v.id) items.push({ id: v.id, quantity: row.qty });
      }.bind(this));
      if (!items.length) return;
      this._bulkAdd(items, this.searchAddBtn, function () {
        this._searchRows = [];
        this._renderSearchTable();
      }.bind(this));
    }

    /* ===================================================================== */
    /* LÄGE B — Klistra in lista                                            */
    /* ===================================================================== */
    _showError(msg) {
      if (!this.errorEl) return;
      if (msg) { this.errorEl.textContent = msg; this.errorEl.hidden = false; }
      else { this.errorEl.textContent = ''; this.errorEl.hidden = true; }
    }

    async _onMatch() {
      if (this._matching) return;
      this._showError('');
      var text = this.textarea ? this.textarea.value : '';
      this.rows = parseInput(text);
      if (!this.rows.length) {
        this._showError('Klistra in minst en rad med ett artikelnummer först.');
        return;
      }
      this._matching = true;
      this.matchBtn.disabled = true;
      var matchLabel = this.matchBtn.textContent;
      this.matchBtn.textContent = 'Matchar …';
      if (this.reviewWrap) this.reviewWrap.hidden = false;
      if (this.progressEl) this.progressEl.hidden = false;
      this._renderReviewTable();

      try {
        for (var i = 0; i < this.rows.length; i++) {
          await this._matchRow(this.rows[i]);
          this._setProgress(i + 1, this.rows.length);
          this._renderReviewTable();
          if (i < this.rows.length - 1) await sleep(MATCH_DELAY_MS);
        }
      } finally {
        if (this.progressEl) this.progressEl.hidden = true;
        this._matching = false;
        this.matchBtn.disabled = false;
        this.matchBtn.textContent = matchLabel;
        this._updateSummary();
      }
    }

    _setProgress(done, total) {
      if (!this.progressEl) return;
      var fill = this.progressEl.querySelector('[data-sb-progress-fill]');
      var txt = this.progressEl.querySelector('[data-sb-progress-text]');
      var pct = total === 0 ? 0 : Math.round((done / total) * 100);
      if (fill) fill.style.width = pct + '%';
      if (txt) txt.textContent = 'Matchar ' + done + ' av ' + total + ' rader …';
    }

    async _matchRow(row) {
      try {
        var products = await fetchProducts(row.article, SEARCH_LIMIT);
        if (products.length) {
          row.match = products[0];
          row.alternatives = products.slice(1);
          row.status = 'matched';
          await this._enrichMatch(row);
        } else {
          row.status = 'no_match';
        }
      } catch (err) {
        row.status = 'error';
      }
    }

    async _enrichMatch(row) {
      if (!row.match) return;
      if (row.match.selectedVariant && row.match.selectedVariant.id) return;
      var detail = await fetchVariants(row.match);
      if (!detail || !detail.variants || !detail.variants.length) return;

      row.match.handle = detail.handle || row.match.handle;
      row.match.variants = detail.variants;
      row.match.product_id = detail.id;

      // Färg-hints som inte matchar någon variant blockerar raden (fel kulör = fel produkt).
      // Storlek/förpackning blockerar aldrig (bara scoring). Mått-tokens matchas löst.
      var missingHints = [];
      (row.hintTokens || []).forEach(function (token) {
        var syns = expandSynonyms(token);
        var anyMatch = detail.variants.some(function (v) {
          var b = variantBlob(v);
          return syns.some(function (s) { return _hasHintLoose(b, s); });
        });
        if (!anyMatch && tokenType(token) === 'color') missingHints.push(token);
      });
      row.missingHints = missingHints;

      var picked = pickVariant(detail.variants, row.hintTokens) || detail.variants[0];
      if (picked && picked.id) {
        row.match.selectedVariant = picked;
        row.variantOutOfStock = !picked.available;
        // Skrev kunden en lös-vara-term (påse/patron/stycken) men vi landade
        // på en kartong? Då säljs varan bara per kartong — flagga så kunden
        // ser det och kan justera antalet (annars risk för överbeställning). PORT.
        var wantedLoose = (row.hintTokens || []).some(function (t) {
          var l = t.toLowerCase();
          return SINGLE_WORDS[l] || FORM_WORDS[l];
        });
        row.soldAsKartong = wantedLoose && variantIsBulk(variantBlob(picked));
      }
    }

    _renderReviewTable() {
      if (!this.tbody) return;
      var self = this;
      var frag = document.createDocumentFragment();

      this.rows.forEach(function (row) {
        var tr = document.createElement('tr');
        tr.className = 'jl-sb__row';
        tr.dataset.status = row.status;
        tr.dataset.removed = String(row.removed);

        // Inmatad rad
        var c1 = document.createElement('td');
        c1.className = 'jl-sb__cell-raw';
        c1.textContent = row.raw;
        tr.appendChild(c1);

        // Match
        var c2 = document.createElement('td');
        if (row.status === 'pending') {
          c2.innerHTML = '<span class="jl-sb__pending">…</span>';
        } else if (row.status === 'matched' && row.match) {
          c2.appendChild(self._buildMatchCell(row));
        } else if (row.status === 'no_match') {
          c2.innerHTML = '<span class="jl-sb__status jl-sb__status--miss">Hittades ej</span>';
        } else if (row.status === 'error') {
          c2.innerHTML = '<span class="jl-sb__status jl-sb__status--miss">Sökfel – prova igen</span>';
        }
        tr.appendChild(c2);

        // Status-pill
        var c3 = document.createElement('td');
        c3.appendChild(self._statusPill(row));
        tr.appendChild(c3);

        // Antal
        var c4 = document.createElement('td');
        if (row.match) {
          var qty = document.createElement('input');
          qty.type = 'number'; qty.min = '1'; qty.value = String(row.qty);
          qty.className = 'jl-input jl-sb__qty'; qty.setAttribute('aria-label', 'Antal');
          qty.addEventListener('input', function (e) {
            var n = parseInt(e.target.value, 10);
            row.qty = isNaN(n) || n < 1 ? 1 : n;
            self._updateSummary();
          });
          c4.appendChild(qty);
        } else {
          c4.textContent = String(row.qty);
        }
        tr.appendChild(c4);

        // Ta bort / ångra
        var c5 = document.createElement('td');
        c5.className = 'jl-sb__cell-action';
        var btn = document.createElement('button');
        btn.type = 'button';
        if (row.removed) {
          btn.className = 'jl-sb__restore';
          btn.textContent = 'Ångra';
        } else {
          btn.className = 'jl-sb__remove';
          btn.setAttribute('aria-label', 'Ta bort rad');
          btn.innerHTML = svgRemove();
        }
        btn.addEventListener('click', function () {
          row.removed = !row.removed;
          self._renderReviewTable();
          self._updateSummary();
        });
        c5.appendChild(btn);
        tr.appendChild(c5);

        frag.appendChild(tr);
      });
      this.tbody.replaceChildren(frag);
    }

    _statusPill(row) {
      var span = document.createElement('span');
      span.className = 'jl-sb__status';
      if (!row.match) {
        span.classList.add('jl-sb__status--miss');
        span.textContent = '✗';
        return span;
      }
      if (row.missingHints && row.missingHints.length) {
        span.classList.add('jl-sb__status--warn');
        span.textContent = '⚠ Välj variant';
        return span;
      }
      if (row.variantOutOfStock) {
        span.classList.add('jl-sb__status--warn');
        span.textContent = '⚠ Slut i lager';
        return span;
      }
      span.classList.add('jl-sb__status--ok');
      span.textContent = '✓ Matchad';
      return span;
    }

    _buildMatchCell(row) {
      var wrap = document.createElement('div');
      wrap.className = 'jl-sb__match';

      var src = row.match.image || (row.match.featured_image && row.match.featured_image.url) || '';
      if (src) {
        var img = document.createElement('img');
        img.className = 'jl-sb__thumb'; img.src = src; img.alt = ''; img.loading = 'lazy';
        wrap.appendChild(img);
      } else {
        var ph = document.createElement('span');
        ph.className = 'jl-sb__thumb jl-sb__thumb--empty';
        wrap.appendChild(ph);
      }

      var body = document.createElement('div');
      body.className = 'jl-sb__match-body';

      var title = document.createElement('a');
      title.className = 'jl-sb__match-title';
      title.href = row.match.url || '#';
      title.target = '_blank'; title.rel = 'noopener';
      title.textContent = row.match.title || '';
      body.appendChild(title);

      // Variantväljare i granskningssteget
      if (row.match.variants && row.match.variants.length > 1) {
        var sel = document.createElement('select');
        sel.className = 'jl-select jl-sb__variant-select';
        sel.setAttribute('aria-label', 'Variant');
        var self = this;
        row.match.variants.forEach(function (vv) {
          var opt = document.createElement('option');
          opt.value = String(vv.id);
          opt.textContent = (vv.title && vv.title !== 'Default Title' ? vv.title : 'Standard')
            + (vv.available ? '' : ' (slut)');
          if (row.match.selectedVariant && vv.id === row.match.selectedVariant.id) opt.selected = true;
          sel.appendChild(opt);
        });
        sel.addEventListener('change', function (e) {
          var id = parseInt(e.target.value, 10);
          row.match.selectedVariant = row.match.variants.find(function (v) { return v.id === id; });
          row.variantOutOfStock = row.match.selectedVariant ? !row.match.selectedVariant.available : false;
          row.missingHints = [];   // kunden valde manuellt → ingen blockering
          row.soldAsKartong = false;   // manuellt val → kunden ser själv vilken förpackning
          self._renderReviewTable();
          self._updateSummary();
        });
        body.appendChild(sel);
      } else {
        var meta = document.createElement('span');
        meta.className = 'jl-sb__match-meta';
        var bits = [];
        if (row.match.vendor) bits.push(row.match.vendor);
        var sv = row.match.selectedVariant;
        if (sv && sv.title && sv.title !== 'Default Title') bits.push(sv.title);
        meta.textContent = bits.join(' · ');
        body.appendChild(meta);
      }

      var price = document.createElement('span');
      price.className = 'jl-sb__match-price';
      var pv = row.match.selectedVariant;
      if (pv && typeof pv.price === 'number') price.textContent = money(pv.price);
      body.appendChild(price);

      // Info: kunden skrev påse/patron/stycken men varan säljs bara per kartong.
      // Antalet avser då kartonger — visa så kunden inte överbeställer. PORT.
      if (row.soldAsKartong) {
        var note = document.createElement('span');
        note.className = 'jl-sb__match-note';
        note.textContent = 'ℹ Säljs endast per kartong — antalet avser kartonger.';
        body.appendChild(note);
      }

      if (row.alternatives && row.alternatives.length) {
        var alt = document.createElement('button');
        alt.type = 'button';
        alt.className = 'jl-sb__alt';
        alt.textContent = 'Annan produkt? (' + row.alternatives.length + ')';
        var self2 = this;
        alt.addEventListener('click', function () {
          var alts = row.alternatives.slice();
          alts.push(row.match);
          row.match = alts[0];
          row.alternatives = alts.slice(1);
          delete row.match.selectedVariant;
          row.missingHints = [];
          self2._renderReviewTable();
          self2._enrichMatch(row).then(function () {
            self2._renderReviewTable();
            self2._updateSummary();
          });
        });
        body.appendChild(alt);
      }

      wrap.appendChild(body);
      return wrap;
    }

    _updateSummary() {
      var addable = 0, totalQty = 0, totalOre = 0, totalGrams = 0;
      var miss = 0, oos = 0, mismatch = 0, errored = 0;
      this.rows.forEach(function (r) {
        if (r.removed) return;
        if (r.status === 'matched' && r.match) {
          if (r.missingHints && r.missingHints.length) { mismatch += 1; return; }
          if (r.variantOutOfStock) { oos += 1; return; }
          var sv = r.match.selectedVariant;
          if (sv && sv.id) {
            addable += 1;
            totalQty += r.qty;
            if (typeof sv.price === 'number') totalOre += sv.price * r.qty;
            totalGrams += (sv.grams || 0) * r.qty;
          }
        } else if (r.status === 'no_match') { miss += 1; }
        else if (r.status === 'error') { errored += 1; }
      });

      if (this.summaryEl) {
        this.summaryEl.replaceChildren();
        this.summaryEl.appendChild(this._chip('ok', addable + ' kan läggas i varukorgen'));
        if (mismatch) this.summaryEl.appendChild(this._chip('warn', mismatch + ' välj variant'));
        if (oos) this.summaryEl.appendChild(this._chip('warn', oos + ' slut i lager'));
        if (miss) this.summaryEl.appendChild(this._chip('miss', miss + ' hittades ej'));
        if (errored) this.summaryEl.appendChild(this._chip('miss', errored + ' sökfel'));
      }
      if (this.totalsEl) this._fillTotals(this.totalsEl, totalOre, totalGrams, totalQty);
      if (this.addBtn) {
        this.addBtn.disabled = addable === 0;
        var lbl = this.addBtn.querySelector('[data-sb-add-label]') || this.addBtn;
        lbl.textContent = totalQty > 0 ? ('Lägg ' + totalQty + ' i varukorgen') : 'Lägg allt i varukorgen';
      }
    }

    _chip(kind, text) {
      var span = document.createElement('span');
      span.className = 'jl-sb__summary-chip jl-sb__summary-chip--' + kind;
      span.textContent = text;
      return span;
    }

    _fillTotals(el, ore, grams, count) {
      el.replaceChildren();
      var rows = [
        ['Antal artiklar', String(count)],
        ['Summa', money(ore)],
        ['Vikt', weightText(grams)]
      ];
      rows.forEach(function (pair) {
        var row = document.createElement('div');
        row.className = 'jl-sb__total-row';
        var dt = document.createElement('span'); dt.textContent = pair[0];
        var dd = document.createElement('strong'); dd.textContent = pair[1];
        row.appendChild(dt); row.appendChild(dd);
        el.appendChild(row);
      });
      var ship = shippingText(grams);
      if (ship) {
        var sr = document.createElement('div');
        sr.className = 'jl-sb__total-row jl-sb__total-row--ship';
        sr.textContent = ship;
        el.appendChild(sr);
      }
    }

    _onAddPaste() {
      var items = [];
      this.rows.forEach(function (r) {
        if (r.removed || r.status !== 'matched' || !r.match) return;
        if (r.missingHints && r.missingHints.length) return;
        if (r.variantOutOfStock) return;
        var sv = r.match.selectedVariant;
        if (sv && sv.id) items.push({ id: sv.id, quantity: r.qty });
      });
      if (!items.length) {
        this._showError('Inga matchade rader att lägga till. Justera artikelnummer eller varianter.');
        return;
      }
      this._showError('');
      this._bulkAdd(items, this.addBtn, function () {
        // Behåll listan men markera klar — kunden kan rensa själv
        try { window.localStorage.removeItem(DRAFT_KEY); } catch (e) {}
      });
    }

    _onReset() {
      this.rows = [];
      this._showError('');
      if (this.textarea) this.textarea.value = '';
      if (this.tbody) this.tbody.replaceChildren();
      if (this.summaryEl) this.summaryEl.replaceChildren();
      if (this.totalsEl) this.totalsEl.replaceChildren();
      if (this.reviewWrap) this.reviewWrap.hidden = true;
      try { window.localStorage.removeItem(DRAFT_KEY); } catch (e) {}
    }

    /* ===================================================================== */
    /* Delad cart-add (bulk → en-och-en-fallback → toast → drawer)          */
    /* ===================================================================== */
    _bulkAdd(items, btn, onDone) {
      var self = this;
      var label = btn ? (btn.querySelector('[data-sb-add-label]') || btn) : null;
      var original = label ? label.textContent : '';
      if (btn) { btn.disabled = true; }
      if (label) label.textContent = 'Lägger till …';

      var addBulk = function () {
        if (window.Jolat && window.Jolat.cart && window.Jolat.cart.add) {
          return window.Jolat.cart.add(items);
        }
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        }).then(function (r) {
          if (!r.ok) return r.json().then(function (e) { return Promise.reject(e); });
          return fetch('/cart.js', { headers: { Accept: 'application/json' } });
        }).then(function (r) { return r.json(); }).then(function (cart) {
          document.dispatchEvent(new CustomEvent('jolat:cart:updated', { detail: cart }));
          document.dispatchEvent(new CustomEvent('jolat:cart:open'));
          return cart;
        });
      };

      addBulk()
        .then(function () {
          self._toast(items.length + ' artiklar tillagda i varukorgen', 'ok');
          if (window.Jolat && window.Jolat.openCart) window.Jolat.openCart();
          if (onDone) onDone();
        })
        .catch(function () {
          // Fallback: en-och-en
          var added = 0;
          var chain = items.reduce(function (p, item) {
            return p.then(function () {
              return fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: item.id, quantity: item.quantity })
              }).then(function (r) { if (r.ok) added += 1; }).catch(function () {});
            });
          }, Promise.resolve());
          // Returnera kedjan så att den avslutande .then() (knappåterställning)
          // väntar tills alla sekventiella add-anrop är klara — annars
          // aktiveras knappen igen medan tilläggen fortfarande pågår (risk
          // för dubbel-add om användaren klickar igen).
          return chain.then(function () {
            var missing = items.length - added;
            if (added > 0) {
              fetch('/cart.js', { headers: { Accept: 'application/json' } })
                .then(function (r) { return r.json(); })
                .then(function (cart) {
                  document.dispatchEvent(new CustomEvent('jolat:cart:updated', { detail: cart }));
                });
              self._toast('✓ ' + added + ' tillagda' + (missing ? ' (' + missing + ' saknas)' : ''), missing ? 'error' : 'ok');
              if (window.Jolat && window.Jolat.openCart) window.Jolat.openCart();
              else document.dispatchEvent(new CustomEvent('jolat:cart:open'));
              if (onDone) onDone();
            } else {
              self._toast('Inga produkter kunde läggas till', 'error');
              self._showError('Inga produkter kunde läggas till. Kontrollera lager och artikelnummer.');
            }
          });
        })
        .then(function () {
          if (btn) btn.disabled = false;
          if (label) label.textContent = original;
        });
    }

    _toast(message, kind) {
      var host = document.querySelector('[data-jolat-toast-host]');
      if (!host) {
        host = document.createElement('div');
        host.setAttribute('data-jolat-toast-host', '');
        host.className = 'jl-toast-host';
        document.body.appendChild(host);
      }
      var node = document.createElement('div');
      node.className = 'jl-toast' + (kind === 'error' ? ' jl-toast--error' : '');
      node.setAttribute('role', 'status');
      node.textContent = message;
      host.appendChild(node);
      void node.offsetWidth;
      node.classList.add('is-shown');
      window.setTimeout(function () {
        node.classList.remove('is-shown');
        window.setTimeout(function () { if (node.parentNode) node.parentNode.removeChild(node); }, 250);
      }, kind === 'error' ? 6000 : 4000);
    }
  }

  if (!customElements.get('jolat-snabborder')) {
    customElements.define('jolat-snabborder', JolatSnabborder);
  }
})();
